// Inteligencia de inventario · la pantalla del carril · I5
//
// Tres cosas y ninguna miente: qué productos ya están bajo su reorden
// sugerido, cuántos tienen cifra y cuántos no (y por qué), y cómo van
// las reglas de ubicación en esta empresa (rechazos, pesos). Buscar un
// producto abre su demanda y su reorden aquí mismo.
import { useCallback, useEffect, useState } from 'react';
import { Brain, RefreshCw } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import EstadoVacio from '../ui/EstadoVacio';
import DemandaYReorden, { ChipSintetico } from './DemandaYReorden';
import DondeAcomodarlo from './DondeAcomodarlo';
import { numero, fecha } from './util';

const NOMBRE_REGLA = {
  cercania_surtido: 'Cerca del surtido',
  misma_familia: 'Misma familia junta',
  pesado_bajo: 'Lo pesado abajo',
  ocupacion: 'Las menos llenas primero',
};

export default function InteligenciaDeInventario() {
  const [resumen, setResumen] = useState(null);
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [soloBajo, setSoloBajo] = useState(true);
  const [elegido, setElegido] = useState(null);

  // Cuándo se consultó: «Volver a consultar» tiene que confirmar que lo
  // hizo aunque siga sin haber nada calculado (auditoría de botones, 19
  // sep 2026: pedía al servidor y no cambiaba nada a la vista).
  const [consultadoA, setConsultadoA] = useState(null);
  const cargar = useCallback(() => {
    Promise.all([api.get('/inteligencia/resumen'), api.get(`/inteligencia/reorden?solo_bajo_reorden=${soloBajo}`)])
      .then(([r, l]) => { setResumen(r); setLista(l); setError(null); setConsultadoA(new Date()); })
      .catch((e) => setError(e.message || 'No se pudo cargar la inteligencia de inventario.'));
  }, [soloBajo]);
  useEffect(() => { cargar(); }, [cargar]);

  if (error) return <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">{error}</p>;
  if (!resumen || !lista) return <p className="text-sm text-gray-500">Cargando…</p>;

  const nadaCalculado = resumen.con_cifra === 0 && resumen.sin_datos === 0;

  return (
    <div className="max-w-5xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Qué comprar y dónde ponerlo</h1>
        <p className="text-sm text-gray-600">Lo que TEGO calcula solo con tus salidas: cuánto se va a vender, cuándo reordenar y dónde acomodar lo que entra.</p>
      </div>
      <Ayuda id="inteligencia.pantalla" />

      {nadaCalculado ? (
        <EstadoVacio
          icono={Brain}
          titulo="Todavía no hay nada calculado"
          descripcion="El cálculo corre cada madrugada a las 04:30 con las salidas registradas (ventas, salidas, mermas). Cuando haya 28 días con salidas y al menos 5 salidas por producto, aquí aparecerán las cifras. Mientras tanto, registra las salidas como siempre: es de ahí de donde aprende."
          accion={(
            <span className="inline-flex flex-col items-center gap-1">
              <button type="button" onClick={cargar} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"><RefreshCw className="h-4 w-4" /> Volver a consultar</button>
              {consultadoA && <span role="status" className="text-xs text-gray-500">Consultado a las {consultadoA.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}: sigue sin haber nada calculado.</span>}
            </span>
          )}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Cifra titulo="Con cifra" valor={resumen.con_cifra} tono="verde" />
            <Cifra titulo="Sin datos" valor={resumen.sin_datos} tono="gris" nota="menos de 28 días o de 5 salidas" />
            <Cifra titulo="Bajo reorden" valor={resumen.bajo_reorden} tono={resumen.bajo_reorden ? 'amarillo' : 'verde'} nota="conviene pedir" />
            <Cifra titulo="Dato al" valor={fecha(resumen.dato_al)} tono="gris" nota={`calculado ${fecha(resumen.calculado_en)}`} />
          </div>
          {resumen.sintetico && (
            <div className="flex flex-wrap items-center gap-2"><ChipSintetico /><Ayuda id="inteligencia.sintetico" /></div>
          )}

          <section className="rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4">
              <div>
                <h2 className="font-semibold text-gray-900">Reorden sugerido</h2>
                <p className="text-xs text-gray-500">Existencia contra el punto de reorden calculado. TEGO avisa; no compra solo.</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={soloBajo} onChange={(e) => setSoloBajo(e.target.checked)} />
                Solo los que ya están por debajo
              </label>
            </div>
            {lista.productos.length === 0 ? (
              <p className="border-t border-gray-100 p-4 text-sm text-gray-600">
                {soloBajo ? 'Ningún producto está por debajo de su reorden sugerido hoy.' : 'Ningún producto tiene reorden calculado todavía.'}
              </p>
            ) : (
              <table className="w-full border-t border-gray-100 text-sm">
                <thead className="text-left text-[11px] uppercase text-gray-500">
                  <tr><th className="px-4 py-2">Producto</th><th className="px-2 py-2 text-right">Existencia</th><th className="px-2 py-2 text-right">Reorden</th><th className="px-2 py-2">Estado</th></tr>
                </thead>
                <tbody>
                  {lista.productos.map((p) => (
                    <tr key={p.id_producto} className="border-t border-gray-100">
                      <td className="px-4 py-2">
                        <button type="button" onClick={() => setElegido(p.id_producto)} className="text-left font-semibold text-[var(--color-marca)]">{p.nombre}</button>
                        <span className="block text-xs text-gray-500">{p.sku}</span>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{numero(p.existencia)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{p.estado === 'OK' ? numero(p.punto_reorden) : '—'}</td>
                      <td className="px-2 py-2">
                        {p.estado !== 'OK' ? <span className="text-gray-500">Sin datos</span>
                          : p.bajo_reorden ? <span className="font-semibold text-amber-700">Por debajo: toca pedir</span>
                          : <span className="text-green-700">Alcanza</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {elegido && (
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Detalle del producto</h2>
                <button type="button" onClick={() => setElegido(null)} className="text-sm text-gray-600">Cerrar</button>
              </div>
              <DemandaYReorden idProducto={elegido} />
              <p className="pt-2 text-sm text-gray-600">Si hoy llega mercancía de este producto:</p>
              <DondeAcomodarlo idProducto={elegido} />
            </section>
          )}

          <section className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="font-semibold text-gray-900">Cómo van las reglas de ubicación</h2>
            <p className="text-xs text-gray-500">
              {resumen.decisiones.aceptadas} aceptadas · {resumen.decisiones.rechazadas} rechazadas · {resumen.decisiones.pendientes} sin decidir. Cada rechazo con motivo baja el peso de esa regla en tu empresa.
            </p>
            {resumen.ajustes.length === 0 ? (
              <p className="mt-2 text-sm text-gray-600">Ninguna regla se ha ajustado todavía: todas pesan lo de fábrica.</p>
            ) : (
              <ul className="mt-2 divide-y divide-gray-100 text-sm">
                {resumen.ajustes.map((a) => (
                  <li key={a.regla} className="flex items-center justify-between py-1.5">
                    <span>{NOMBRE_REGLA[a.regla] || a.regla}</span>
                    <span className="text-gray-600">peso {numero(a.peso, 2)} · {a.rechazos} rechazos · {a.aceptaciones} aceptaciones</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3"><Ayuda id="inteligencia.ficha" /></div>
          </section>
        </>
      )}
    </div>
  );
}

function Cifra({ titulo, valor, tono, nota }) {
  const clase = { verde: 'text-green-700', amarillo: 'text-amber-700', gris: 'text-gray-700' }[tono] || 'text-gray-700';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <p className="text-[11px] font-semibold uppercase text-gray-500">{titulo}</p>
      <p className={`text-xl font-semibold tabular-nums ${clase}`}>{valor}</p>
      {nota && <p className="text-[11px] text-gray-500">{nota}</p>}
    </div>
  );
}
